import React, { useState, useEffect, useMemo } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/config";
import { S, STAGES, STAGE_COLOR } from "../../crm/theme";
import { fetchLeads, createLead, updateLead, deleteLead, createTask, visibleTo, appendLeadNote, completeOpenTasksForLead } from "../../crm/crmApi";
import { STAGE_BUTTONS, dateInDays } from "../../crm/pipelineActions";
import LeadModal from "../../crm/LeadModal";
import ActionModal from "../../crm/ActionModal";

export default function CrmPipelineScreen({ user }) {
  const [leads, setLeads] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [modalLead, setModalLead] = useState(undefined); // undefined = closed, null = new, object = edit
  const [activeAction, setActiveAction] = useState(null); // { lead, action } while the choice pop-up is open

  const load = async () => {
    setLoading(true);
    try {
      const [allLeads, usersSnap] = await Promise.all([
        fetchLeads(),
        getDocs(collection(db, "platformUsers")),
      ]);
      setLeads(visibleTo(allLeads, user));
      setSalesUsers(
        usersSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((u) => u.role === "Sales" || u.role === "SalesHead")
      );
    } catch (e) {
      setMsg("Error loading pipeline: " + e.message);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [user]);

  const byStage = useMemo(() => {
    const map = Object.fromEntries(STAGES.map((s) => [s, []]));
    leads.forEach((l) => { (map[l.stage] || map["New Lead"]).push(l); });
    return map;
  }, [leads]);

  const handleSave = async (data) => {
    if (modalLead && modalLead.id) {
      await updateLead(modalLead.id, data);
      setMsg("Lead updated.");
    } else {
      await createLead(data, user);
      setMsg("Lead added.");
    }
    await load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this lead? This cannot be undone.")) return;
    await deleteLead(id);
    setModalLead(undefined);
    setMsg("Lead deleted.");
    await load();
  };

  // Runs when the sales person saves a choice in the pop-up:
  //   1. writes a dated note  2. moves the stage (if the choice says so)
  //   3. marks old open tasks done  4. creates the next follow-up task
  const handleConfirm = async ({ choice, note, dueDate, subscription, onboarding, revisitDate }) => {
    const lead = activeAction.lead;

    const noteText = note ? `${choice.label} — ${note}` : choice.label;
    const updates = { notes: appendLeadNote(lead.notes, noteText, user) };

    if (choice.nextStage) updates.stage = choice.nextStage;
    if (choice.askValue) {
      updates.subscriptionValue = subscription;
      updates.onboardingFee = onboarding;
    }
    if (choice.nextStage === "Lost") {
      updates.lostReason = choice.lostReason || "";
      updates.revisitDate = revisitDate || "";
    }

    await updateLead(lead.id, updates);
    await completeOpenTasksForLead(lead.id);

    const assignee = {
      assignedToUid: lead.assignedToUid || "",
      assignedToName: lead.assignedToName || "",
    };

    if (choice.task) {
      await createTask(
        {
          title: `${choice.task.title} ${lead.societyName}`,
          leadId: lead.id,
          dueDate: choice.askDate ? dueDate : dateInDays(choice.task.days),
          priority: "High",
          notes: "",
          ...assignee,
        },
        user
      );
    }

    if (choice.nextStage === "Lost" && revisitDate) {
      await createTask(
        {
          title: `Revisit ${lead.societyName} (earlier lost)`,
          leadId: lead.id,
          dueDate: revisitDate,
          priority: "Low",
          notes: "",
          ...assignee,
        },
        user
      );
    }

    setActiveAction(null);
    setMsg(
      choice.nextStage && choice.nextStage !== lead.stage
        ? `"${lead.societyName}" moved to ${choice.nextStage}.`
        : `"${lead.societyName}" updated.`
    );
    await load();
  };

  const toneStyle = (tone) => {
    if (tone === "won") return { background: "#1a3a2a", color: "#7ce0ad" };
    if (tone === "lost") return { background: "#3a1a1a", color: "#f87171" };
    return {};
  };

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div style={S.title}>🧭 Sales Pipeline</div>
        <button style={S.btnBlue} onClick={() => setModalLead(null)}>+ New Lead</button>
      </div>

      {msg && <div style={S.msg}>{msg}</div>}
      {loading ? (
        <div style={{ color: "#7b82a0" }}>Loading…</div>
      ) : (
        <div style={{ display: "flex", gap: 14, overflowX: "auto", paddingBottom: 12 }}>
          {STAGES.map((stage) => {
            const cards = byStage[stage] || [];
            const buttons = STAGE_BUTTONS[stage] || [];
            return (
              <div
                key={stage}
                style={{
                  minWidth: 240, flex: "0 0 240px", background: "#14161f",
                  border: "1px solid #2e3250",
                  borderRadius: 10, padding: 10, display: "flex", flexDirection: "column",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: STAGE_COLOR[stage] }}>{stage}</div>
                  <div style={{ fontSize: 11, color: "#7b82a0" }}>{cards.length}</div>
                </div>
                <div style={{ marginBottom: 10 }} />

                <div style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 40 }}>
                  {cards.map((lead) => (
                    <div
                      key={lead.id}
                      style={{
                        background: "#1a1d27", border: "1px solid #2e3250", borderRadius: 8, padding: 10,
                        borderLeft: `3px solid ${STAGE_COLOR[stage]}`,
                      }}
                    >
                      <div onClick={() => setModalLead(lead)} style={{ cursor: "pointer" }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#e8eaf0", marginBottom: 3 }}>{lead.societyName}</div>
                        {lead.city && <div style={{ fontSize: 11, color: "#7b82a0", marginBottom: 4 }}>{lead.city}</div>}
                        <div style={{ fontSize: 10, color: "#7b82a0", marginTop: 4 }}>{lead.assignedToName || "Unassigned"}</div>
                      </div>

                      {buttons.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                          {buttons.map((b) => (
                            <button
                              key={b.label}
                              style={{
                                ...S.btnSmall,
                                flex: b.tone ? "1 1 40%" : "1 1 100%",
                                ...toneStyle(b.tone),
                              }}
                              onClick={() => setActiveAction({ lead, action: b.action })}
                            >
                              {b.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalLead !== undefined && (
        <LeadModal
          lead={modalLead}
          user={user}
          salesUsers={salesUsers}
          onSave={handleSave}
          onClose={() => setModalLead(undefined)}
          onDelete={handleDelete}
        />
      )}

      {activeAction && (
        <ActionModal
          lead={activeAction.lead}
          action={activeAction.action}
          onConfirm={handleConfirm}
          onClose={() => setActiveAction(null)}
        />
      )}
    </div>
  );
}
